import { useState } from "react";
import { toast } from "sonner";
import { createBooking } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight } from "lucide-react";

const SEGMENTS = [
  { value: "small-business", label: "I'm a small business owner" },
  { value: "hr-manager", label: "I lead HR at an organization" },
  { value: "public-sector", label: "I'm in the public sector" },
];

const SERVICES = [
  { value: "pay-equity", label: "Pay Equity Compliance" },
  { value: "fractional", label: "Fractional HR Director" },
  { value: "wage-studies", label: "Wage & Market Study" },
  { value: "other", label: "Not sure yet" },
];

export const BookingForm = ({ defaultService = "", compact = false }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", organization: "", segment: "",
    service_interest: defaultService, preferred_time: "", message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.segment || !form.preferred_time) {
      toast.error("Please fill in your name, email, audience and a preferred time.");
      return;
    }
    setLoading(true);
    try {
      await createBooking(form);
      toast.success("Request received. Susan will reach out to confirm your call.");
      setForm({ name: "", email: "", organization: "", segment: "", service_interest: defaultService, preferred_time: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-forest-900 border-forest-700 text-sage-100 placeholder:text-sage-400 focus-visible:ring-golden-500 rounded-sm h-12";

  return (
    <form onSubmit={submit} data-testid="booking-form" className="space-y-5">
      <div className={compact ? "space-y-5" : "grid sm:grid-cols-2 gap-5"}>
        <div className="space-y-2">
          <Label className="text-sage-200 text-sm">Name</Label>
          <Input data-testid="booking-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Rivera" className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-sage-200 text-sm">Email</Label>
          <Input data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" className={inputCls} />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sage-200 text-sm">Organization (optional)</Label>
        <Input data-testid="booking-org" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="City of Bend / Acme Co." className={inputCls} />
      </div>

      <div className={compact ? "space-y-5" : "grid sm:grid-cols-2 gap-5"}>
        <div className="space-y-2">
          <Label className="text-sage-200 text-sm">Which best describes you?</Label>
          <Select value={form.segment} onValueChange={(v) => set("segment", v)}>
            <SelectTrigger data-testid="booking-segment" className={inputCls}>
              <SelectValue placeholder="Select audience" />
            </SelectTrigger>
            <SelectContent className="bg-forest-900 border-forest-700 text-sage-100">
              {SEGMENTS.map((s) => (
                <SelectItem key={s.value} value={s.value} className="focus:bg-forest-800 focus:text-golden-500">{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sage-200 text-sm">Interested in</Label>
          <Select value={form.service_interest} onValueChange={(v) => set("service_interest", v)}>
            <SelectTrigger data-testid="booking-service" className={inputCls}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-forest-900 border-forest-700 text-sage-100">
              {SERVICES.map((s) => (
                <SelectItem key={s.value} value={s.value} className="focus:bg-forest-800 focus:text-golden-500">{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sage-200 text-sm">Preferred time</Label>
        <Input data-testid="booking-time" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} placeholder="Weekday mornings, PST" className={inputCls} />
      </div>

      <div className="space-y-2">
        <Label className="text-sage-200 text-sm">What's on your mind? (optional)</Label>
        <Textarea data-testid="booking-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="A quick note about your situation..." className={`${inputCls} h-28 py-3`} />
      </div>

      <button
        type="submit"
        data-testid="booking-submit"
        disabled={loading}
        className="group w-full flex items-center justify-center gap-2 bg-golden-500 text-navy font-semibold h-13 py-4 rounded-sm transition-colors duration-300 hover:bg-golden-600 disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Request my discovery call <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
};
